<template>
    <modal :show="show" :showLarge="step === 3">
        <div slot="modalHeader">
          <modal-header :closeModal="customClose">{{ title }}</modal-header>
        </div>
        <spinner-icon v-if="loading" type="upload" />
        <notifications :messages="notifications" v-if="notifications && notifications.length > 0"/>
        <steps :options="steps.options" :active="step" />
        <div v-if="step === 1">
          <form-group>
            <field name="Name" value="" v-model="newCampaign.name" :error="errors.campaignName"/>
          </form-group>
          <form-group>
            <field name="Campaign Job Number (Optional)" value="" v-model="newCampaign.campaign_job_number"/>
          </form-group>
          <form-group>
            <dropdown name="Client" :options="clientOptions" v-model="newCampaign.client" :current="newCampaign.client"/>
          </form-group>
        </div>
        <div v-else-if="step === 2">
          <form-group>
            <field name="Name" value="" v-model="newCreative.name" :error="errors.creativeName"/>
          </form-group>
          <form-group>
            <field name="Creative Job Number (Optional)" value="" v-model="newCreative.creative_job_number" />
          </form-group>
          <upload-file name="Master File" :ctaText="uploadText" @input="onUploadFile" acceptedFileType="application/zip" :error="errors.creativeFile" />
          <br />
          <form-group>
            <dropdown name="Type" :options="creativeOptions" v-model="newCreative.type" current="html" />
          </form-group>
        </div>
        <div v-else-if="step === 3 && formattedCreative">
          <creative-table :tableData="formattedCreative.fields" v-model="formattedCreative.fields" @onToggleParentCheckboxes="updateCheckboxData" />
        </div>
        <div slot="modalFooter">
          <modal-footer :closeModal="customClose" :confirm="save" :hideCancelText="step === 3" :confirmText="step === 3 ? 'Complete' : 'Next'"/>
        </div>
    </modal>
</template>

<script>
import Modal from '@/components/Modal/modal'
import ModalHeader from '@/components/Modal/modal-header'
import ModalFooter from '@/components/Modal/modal-footer'
import ButtonCta from '@/components/Button/button'
import FormGroup from '@/components/Form/FormGroup/form-group'
import Dropdown from '@/components/Form/Dropdown/dropdown'
import Steps from '@/components/Steps/steps'
import UploadFile from '@/components/UploadFile/upload-file'
import Heading from '@/components/Heading/heading'
import Field from '@/components/Form/Field/field'
import Notifications from '@/components/Notifications/notifications'
import CreativeTable from '@/components/CreativeTable/creative-table'
import SpinnerIcon from '@/components/SpinnerIcon/spinner'

import {modalMixin} from '@/mixins/modalMixin'
import {campaignMixin} from '@/mixins/campaignMixin'
import {creativeMixin} from '@/mixins/creativeMixin'
import {notificationMixin} from '@/mixins/notificationMixin'

import {omit, map, assoc} from 'ramda'

export default {
  name: 'modal_create_campaign',
  mixins: [modalMixin, campaignMixin, creativeMixin, notificationMixin],
  components: {
    Modal,
    ModalHeader,
    ModalFooter,
    ButtonCta,
    Steps,
    FormGroup,
    Dropdown,
    Field,
    UploadFile,
    Heading,
    CreativeTable,
    Notifications,
    SpinnerIcon
  },
  props: {
    show: Boolean,
    goToStep: {
      type: Number,
      default: 1
    },
    confirmId: String
  },
  data: () => ({
    step: 0,
    uploadText: 'Upload',
    files: null,
    table: {
      columns: {
        isEditable: 'allCheckboxes',
        name: 'Name',
        type: 'Type',
        charLimit: 'Limit',
        isCharLimitHard: 'HardLimit',
        defaultValue: 'Default Value'
      }
    },
    newCampaign: {
      name: '',
      client: '',
      campaign_job_number: ''
    },
    newCreative: {
      name: '',
      type: 'html',
      creative_job_number: ''
    },
    creativeOptions: [
      {
        name: 'HTML',
        value: 'html'
      },
      {
        name: 'PSD',
        value: 'psd'
      },
      {
        name: 'VIDEO (HTML)',
        value: 'html-video'
      }
    ],
    errors: {
      campaignName: '',
      creativeName: '',
      creativeFile: ''
    }
  }),
  mounted () {
    this.step = this.goToStep
    this.newCampaign.client = this.currentClient
  },
  watch: {
    goToStep: function () {
      this.step = this.goToStep
    }
  },
  computed: {
    title () {
      return `Create ${this.step === 1 ? 'Campaign' : 'Creative'}`
    },
    currentClient () {
      return this.$route.params.id
    },
    formattedCreative () {
      const newCreative = this.creative
      newCreative.fields = map(item => assoc('editableActions', true, item), this.creative.fields)
      return newCreative
    }
  },
  methods: {
    onUploadFile (e) {
      const files = e.target.files || e.dataTransfer.files
      this.uploadText = e.target.files[0].name
      this.files = files
    },
    save () {
      if (this.step === 1) {
        this.saveCampaign()
      }
      if (this.step === 2) {
        this.saveCreative()
      }
      if (this.step === 3) {
        this.completeCreative('complete')
      }
    },
    saveCampaign () {
      if (this.newCampaign.name === '') {
        this.errors.campaignName = 'This value should not be blank.'
        return
      }
      this.addCampaign(this.newCampaign).then(() => {
        this.step = 2
      }, error => {
        this.close()
        this.addNotification('danger', error)
      })
    },
    resetErrors () {
      this.errors.campaignName = ''
      this.errors.creativeName = ''
      this.errors.creativeFile = ''
    },
    saveCreative () {
      let creativeErrors = 0

      this.resetErrors()

      if (this.newCreative.name === '') {
        this.errors.creativeName = 'This value should not be blank.'
        creativeErrors++
      }
      if (!this.files) {
        this.errors.creativeFile = 'You need to upload a master file.'
        creativeErrors++
      }

      if (creativeErrors) {
        return
      }
      this.startLoading()
      const formData = new FormData()
      formData.append('name', this.newCreative.name)
      formData.append('type', this.newCreative.type)
      formData.append('creative_job_number', this.newCreative.creative_job_number)
      formData.append('campaign', this.campaign.hash_id)
      formData.append('file', this.files[0])
      this.addCreative(formData).then((creative) => {
        const masters = {}
        const payload = {
          id: creative.hash_id,
          columns: ['id', 'master', 'locale', 'width', 'height'],
          unit: []
        }
        creative.masters.forEach(master => {
          masters[master.name] = {
            values: [this.uniqueId(), master.name, master.name, master.width, master.height]
          }
        })
        creative.fields.forEach(field => {
          payload.columns.push(field.name)
          for (const master in masters) {
            let value = ''
            field.default_values.forEach(defaultValue => {
              if (defaultValue.master === master) {
                value = defaultValue.value
              }
            })
            masters[master].values.push(value)
          }
        })
        for (const master in masters) {
          payload.unit.push(masters[master].values)
        }
        this.updateCreativeUnit(payload).then(() => {
          this.step = 3
          this.addNotification('success', 'Creative successfully created.')
          this.finishLoading()
        }, error => {
          this.finishLoading()
          this.close()
          this.addNotification('danger', error)
        })
      }, error => {
        this.finishLoading()
        this.close()
        this.addNotification('danger', error)
      })
    },
    completeCreative (action) {
      this.startLoading()
      const updateCreative = omit(['campaign', 'date_created', 'date_updated', 'type', 'units', 'editableActions'], this.formattedCreative)
      this.updateCreative(updateCreative).then(() => {
        if (action === 'complete') {
          this.$router.push(`/creatives/${updateCreative.hash_id}/bulk`)
        } else {
          this.$router.push(`/creatives/${updateCreative.hash_id}/bulk`) // TODO Push to current page
        }
      }, error => {
        this.finishLoading()
        this.addNotification('danger', error)
      })
      this.close()
    },
    customClose () {
      if (this.step === 3) {
        console.log('custom step')
        this.completeCreative('close')
      } else {
        this.close()
      }
    }
  }
}
</script>

<style lang="scss">
</style>



// WEBPACK FOOTER //
// src/components/Modal/campaigns/modal-create-campaign.vue